import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { createMission, updateMission, listPools, listPartnerLinks, listPilotNeeds, extractRfp, generateMissionSections, previewMissionEmail } from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import { FieldLabel as UIFieldLabel } from "../../../components/Portal/ui";
import RichTextEditor from "../../../components/Portal/RichTextEditor";

// This modal's labels use a 6px bottom margin (vs the shared default 4px);
// thin wrapper preserves that spacing while consuming the shared component.
const FieldLabel = ({ sx, ...props }) => (
  <UIFieldLabel sx={{ marginBottom: "6px !important", ...(sx || {}) }} {...props} />
);

// Section heading inside a token block.
const SectionTitle = ({ children, ...rest }) => (
  <Text
    fontSize="13px"
    fontWeight="800"
    color="portal.text"
    letterSpacing="-0.01em"
    sx={{ marginBottom: "0 !important" }}
    {...rest}
  >
    {children}
  </Text>
);

// Helper text under a label/section.
const HelpText = ({ children, ...rest }) => (
  <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }} {...rest}>
    {children}
  </Text>
);

// Token block wrapper for grouped sub-sections (rate groups, eval config, etc.).
const TokenBlock = ({ children, ...rest }) => (
  <Box
    borderWidth="1px"
    borderColor="portal.line"
    borderRadius="10px"
    bg="portal.soft"
    p="14px"
    {...rest}
  >
    {children}
  </Box>
);

// Shared sizing for the native Select so it lines up at ~31px like token inputs.
const SELECT_SX = {
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  height: "31px !important",
  fontSize: "13px",
};

// Tokenized horizontal step indicator (numbered dots + labels, navy active).
const StepIndicator = ({ activeStep, labels, onStepClick }) => (
  <HStack spacing="8px" mb="20px" align="center" data-testid="mission-form-steps">
    {labels.map((label, idx) => {
      const isActive = idx === activeStep;
      const isDone = idx < activeStep;
      const clickable = isDone && typeof onStepClick === "function";
      return (
        <React.Fragment key={idx}>
          <HStack
            spacing="8px"
            align="center"
            cursor={clickable ? "pointer" : "default"}
            onClick={clickable ? () => onStepClick(idx) : undefined}
          >
            <Flex
              align="center"
              justify="center"
              boxSize="22px"
              borderRadius="999px"
              fontSize="11px"
              fontWeight="800"
              bg={isActive || isDone ? "portal.primary" : "portal.soft"}
              color={isActive || isDone ? "white" : "portal.muted"}
              borderWidth="1px"
              borderColor={isActive || isDone ? "portal.primary" : "portal.line"}
              flexShrink={0}
            >
              {idx + 1}
            </Flex>
            <Text
              display={{ base: "none", md: "block" }}
              fontSize="12px"
              fontWeight={isActive ? "800" : "600"}
              color={isActive ? "portal.text" : "portal.muted"}
              whiteSpace="nowrap"
              sx={{ marginBottom: "0 !important" }}
            >
              {label}
            </Text>
          </HStack>
          {idx < labels.length - 1 && (
            <Box flex="1" h="1px" bg="portal.line" minW="12px" />
          )}
        </React.Fragment>
      );
    })}
  </HStack>
);

const STEPS = [
  { key: "purpose", titleKey: "partners.missions.form.steps.purpose" },
  { key: "details", titleKey: "partners.missions.form.steps.details" },
  { key: "skills", titleKey: "partners.missions.form.steps.skills" },
  { key: "conditions", titleKey: "partners.missions.form.steps.conditions" },
  { key: "recipients", titleKey: "partners.missions.form.steps.recipients" },
  { key: "review", titleKey: "partners.missions.form.steps.review" },
];

const DIFFICULTY_OPTIONS = [
  "Junior",
  "Confirmé",
  "Senior",
  "Senior confirmé",
  "Expert",
];

const IMPORTANCE_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
];

const EVALUATION_LANGUAGES = [
  { value: "Français", label: "Français" },
  { value: "English", label: "English" },
];

const MAX_EVAL_SKILLS = 5;

const PURPOSE_OPTIONS = [
  { value: "contact_pool", labelKey: "partners.missions.form.purposeOptions.contactPool", label: "Share an assignment with a list of candidates" },
  { value: "propose_assignment", labelKey: "partners.missions.form.purposeOptions.proposeAssignment", label: "Propose an assignment to a partner company" },
];

const TJM_CONTRACT_TYPES = ["freelance", "portage", "sous_traitance"];

const CONTRACT_TYPE_OPTIONS = [
  { value: "freelance", labelKey: "partners.missions.form.contractTypeFreelance", label: "Freelance" },
  { value: "cdi", labelKey: "partners.missions.form.contractTypeCdi", label: "CDI" },
  { value: "portage", labelKey: "partners.missions.form.contractTypePortage", label: "Portage salarial" },
  { value: "sous_traitance", labelKey: "partners.missions.form.contractTypeSousTraitance", label: "Sous-traitance" },
];

const INITIAL_FORM = {
  purpose: "contact_pool",
  title: "",
  description: "",
  skillsTechnical: "",
  skillsFunctional: "",
  skillsBusiness: "",
  requiredSkills: "",
  optionalSkills: "",
  location: "",
  tjmMin: "",
  tjmMax: "",
  salaireMin: "",
  salaireMax: "",
  tjmSelonProfil: false,
  salaireSelonProfil: false,
  startDate: "",
  startAsap: false,
  durationDays: "",
  durationMonths: "",
  durationUnit: "days",
  isRenewable: false,
  contractTypes: [],
  anonymizationConfig: "{}",
  atsThreshold: "none",
  clientNeedId: "",
  evaluationEnabled: false,
  evaluationAutoMode: false,
  evaluationLanguage: "Français",
  evaluationJobMatrix: [],
  recipientPoolIds: [],
  recipientPartnerCompanyIds: [],
  sendDirectly: true,
  generatedSections: { responsibilities: "", requiredSkills: "", candidateProfile: "" },
  customSubject: "",
  customEmailHtml: "",
};

const EMPTY_SECTIONS = { responsibilities: "", requiredSkills: "", candidateProfile: "" };

// Parse the mission.generatedSections JSON string into the editable section object.
const parseGeneratedSections = (raw) => {
  if (!raw) return { ...EMPTY_SECTIONS };
  try {
    const v = typeof raw === "string" ? JSON.parse(raw) : raw;
    return {
      responsibilities: v?.responsibilities || "",
      requiredSkills: v?.requiredSkills || "",
      candidateProfile: v?.candidateProfile || "",
    };
  } catch {
    return { ...EMPTY_SECTIONS };
  }
};

const sectionsAreEmpty = (s) =>
  !s || (!s.responsibilities?.trim() && !s.requiredSkills?.trim() && !s.candidateProfile?.trim());

const MissionFormModal = ({ isOpen, onClose, mission, onSuccess }) => {
  const toast = useToast();
  const { t, i18n } = useTranslation("portal");
  const { selectedBusinessUnitId, features } = useBusinessUnit();
  const isEdit = Boolean(mission);
  const hasPilot = Boolean(features?.has_pilot);
  const hasSkills = Boolean(features?.has_skills);

  const [activeStep, setActiveStep] = useState(0);
  const goToNext = () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goToPrevious = () => setActiveStep((s) => Math.max(s - 1, 0));

  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pools, setPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(false);
  const [partnerLinks, setPartnerLinks] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [pilotNeeds, setPilotNeeds] = useState([]);
  const [loadingPilotNeeds, setLoadingPilotNeeds] = useState(false);
  const [rfpFile, setRfpFile] = useState(null);
  const [rfpLoading, setRfpLoading] = useState(false);
  const [rfpDragActive, setRfpDragActive] = useState(false);
  const rfpInputRef = useRef(null);
  const rfpDragCounter = useRef(0);
  const [rfpConfirmData, setRfpConfirmData] = useState(null);
  const rfpConfirmCancelRef = useRef(null);

  // Skill tag input states (3 categories + required/optional)
  const [skillInput, setSkillInput] = useState({
    technical: "",
    functional: "",
    business: "",
  });

  // AI prose generation (Review step) — sections auto-generate once on entering
  // the review step; there is no manual regenerate control.
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const sectionsAutoTriedRef = useRef(false);
  const sectionsEditedRef = useRef(false);

  // Live email preview (Review step) — rendered by the backend from the draft.
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const previewDebounceRef = useRef(null);
  const previewSeqRef = useRef(0);

  // Full email customization (Review step).
  const [previewSubject, setPreviewSubject] = useState(""); // generated subject from the backend
  const [defaultBodyHtml, setDefaultBodyHtml] = useState(""); // generated body, to seed the editor
  const [subjectDirty, setSubjectDirty] = useState(false); // recruiter edited the subject
  const [customizeEmail, setCustomizeEmail] = useState(false); // recruiter is editing the full email body

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveStep(0);
      if (mission) {
        setForm({
          purpose: mission.purpose || "contact_pool",
          title: mission.title || "",
          description: mission.description || "",
          skillsTechnical: mission.skillsTechnical || "",
          skillsFunctional: mission.skillsFunctional || "",
          skillsBusiness: mission.skillsBusiness || "",
          requiredSkills: mission.requiredSkills || "",
          optionalSkills: mission.optionalSkills || "",
          location: mission.location || "",
          tjmMin: mission.tjmMin ?? "",
          tjmMax: mission.tjmMax ?? "",
          salaireMin: mission.salaireMin ?? "",
          salaireMax: mission.salaireMax ?? "",
          tjmSelonProfil: mission.tjmSelonProfil ?? false,
          salaireSelonProfil: mission.salaireSelonProfil ?? false,
          startDate: mission.startDate || "",
          startAsap: !!mission.startAsap,
          durationDays: mission.durationDays ?? "",
          durationMonths: mission.durationMonths ?? "",
          durationUnit: mission.durationUnit || (mission.durationMonths ? "months" : "days"),
          isRenewable: mission.isRenewable ?? false,
          contractTypes:
            Array.isArray(mission.contractTypes) && mission.contractTypes.length > 0
              ? mission.contractTypes
              : mission.contractType
              ? [mission.contractType]
              : [],
          anonymizationConfig: mission.anonymizationConfig || "{}",
          atsThreshold: mission.atsThreshold || "none",
          evaluationEnabled: (() => {
            try { return JSON.parse(mission.evaluationConfig || "{}").enabled || false; } catch { return false; }
          })(),
          evaluationAutoMode: (() => {
            try { return JSON.parse(mission.evaluationConfig || "{}").auto_mode ?? false; } catch { return false; }
          })(),
          evaluationLanguage: (() => {
            try { return JSON.parse(mission.evaluationConfig || "{}").language || "Français"; } catch { return "Français"; }
          })(),
          evaluationJobMatrix: (() => {
            try { return JSON.parse(mission.evaluationConfig || "{}").job_matrix || []; } catch { return []; }
          })(),
          recipientPoolIds: (mission.recipients || [])
            .filter((r) => r.recipientType === "pool" && r.poolId)
            .map((r) => r.poolId),
          clientNeedId: mission.clientNeedId ?? "",
          recipientPartnerCompanyIds: (mission.recipients || [])
            .filter((r) => r.recipientType === "partner_company" && r.partnerCompanyId)
            .map((r) => r.partnerCompanyId),
          sendDirectly: true,
          generatedSections: parseGeneratedSections(mission.generatedSections),
          customSubject: mission.customSubject || "",
          customEmailHtml: mission.customEmailHtml || "",
        });
      } else {
        setForm(INITIAL_FORM);
      }
      // A mission already carrying overrides opens in the customized state.
      setSubjectDirty(Boolean(mission?.customSubject));
      setCustomizeEmail(Boolean(mission?.customEmailHtml));
      setPreviewSubject("");
      setDefaultBodyHtml("");
      setSkillInput({ technical: "", functional: "", business: "" });
      setRfpFile(null);
      setRfpLoading(false);
      setRfpDragActive(false);
      setRfpConfirmData(null);
      rfpDragCounter.current = 0;
      sectionsAutoTriedRef.current = false;
      sectionsEditedRef.current = false;
      setSectionsLoading(false);
      setPreviewHtml("");
      setPreviewLoading(false);
      setPreviewError(false);
      previewSeqRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mission]);

  // When the modal closes, reset the wizard step so a reopen never starts on a
  // stale step. The component stays mounted across open/close, so without this the
  // reopen render could still read activeStep === Review and fire the auto-generate
  // effect prematurely (marking generation "tried" and skipping the real run).
  useEffect(() => {
    if (!isOpen) {
      setActiveStep(0);
      sectionsAutoTriedRef.current = false;
    }
  }, [isOpen]);

  // Fetch pools for recipient selection
  const fetchPools = useCallback(async () => {
    setLoadingPools(true);
    try {
      const res = await listPools(
        { pageSize: 100 },
        { buId: selectedBusinessUnitId || undefined }
      );
      setPools(res.items || []);
    } catch {
      setPools([]);
    } finally {
      setLoadingPools(false);
    }
  }, [selectedBusinessUnitId]);

  const fetchPartnerLinks = useCallback(async () => {
    setLoadingPartners(true);
    try {
      const res = await listPartnerLinks(
        { pageSize: 100 },
        { buId: selectedBusinessUnitId || undefined }
      );
      setPartnerLinks(res.items || []);
    } catch {
      setPartnerLinks([]);
    } finally {
      setLoadingPartners(false);
    }
  }, [selectedBusinessUnitId]);

  const fetchPilotNeeds = useCallback(async () => {
    if (!hasPilot) return;
    setLoadingPilotNeeds(true);
    try {
      const res = await listPilotNeeds({
        buId: selectedBusinessUnitId || undefined,
      });
      setPilotNeeds(res.items || []);
    } catch {
      setPilotNeeds([]);
    } finally {
      setLoadingPilotNeeds(false);
    }
  }, [hasPilot, selectedBusinessUnitId]);

  useEffect(() => {
    if (isOpen) {
      fetchPools();
      fetchPartnerLinks();
      if (hasPilot) fetchPilotNeeds();
    }
  }, [isOpen, fetchPools, fetchPartnerLinks, hasPilot, fetchPilotNeeds]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePilotNeedSelect = (needId) => {
    if (!needId) {
      setForm((prev) => ({ ...prev, clientNeedId: "" }));
      return;
    }
    const need = pilotNeeds.find((n) => n.id === Number(needId));
    if (!need) {
      setForm((prev) => ({ ...prev, clientNeedId: needId }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      clientNeedId: needId,
      title: need.title || prev.title,
      description: need.description || prev.description,
      skillsTechnical: need.skillsTechnical || prev.skillsTechnical,
      skillsFunctional: need.skillsFunctional || prev.skillsFunctional,
      skillsBusiness: need.skillsBusiness || prev.skillsBusiness,
      requiredSkills: need.requiredSkills || prev.requiredSkills,
      optionalSkills: need.optionalSkills || prev.optionalSkills,
      // An ASAP need stays ASAP on the mission instead of inheriting the day it was flagged.
      startAsap: need.asap ? true : need.startDate ? false : prev.startAsap,
      startDate: need.asap ? "" : need.startDate || prev.startDate,
    }));
  };

  // RFP upload handler
  const VALID_RFP_MIMES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const handleRfpFile = async (file) => {
    if (!file) return;
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    if (!["pdf", "docx"].includes(ext) && !VALID_RFP_MIMES.includes(file.type)) {
      toast({ title: t("partners.missions.toasts.rfpUnsupported", "Only PDF and DOCX files are supported"), status: "warning" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: t("partners.missions.toasts.rfpTooLarge", "File too large (max 10 MB)"), status: "warning" });
      return;
    }
    setRfpFile(file);
    setRfpLoading(true);
    try {
      const result = await extractRfp(file, { buId: selectedBusinessUnitId || undefined });
      // Check if any existing fields would be overwritten
      const hasExistingData = form.title.trim() || form.description.trim();
      const hasExtractedData = result.title || result.description;
      if (hasExistingData && hasExtractedData) {
        // Show confirmation dialog — store extracted data for later application
        setRfpConfirmData(result);
        setRfpLoading(false);
        return;
      }
      applyRfpData(result);
    } catch {
      toast({ title: t("partners.missions.toasts.rfpError", "Failed to extract RFP data"), status: "error" });
    } finally {
      setRfpLoading(false);
    }
  };

  const applyRfpData = (result) => {
    setForm((prev) => ({
      ...prev,
      title: result.title || prev.title,
      description: result.description || prev.description,
      skillsTechnical: Array.isArray(result.skillsTechnical) && result.skillsTechnical.length > 0 ? JSON.stringify(result.skillsTechnical) : prev.skillsTechnical,
      skillsFunctional: Array.isArray(result.skillsFunctional) && result.skillsFunctional.length > 0 ? JSON.stringify(result.skillsFunctional) : prev.skillsFunctional,
      skillsBusiness: Array.isArray(result.skillsBusiness) && result.skillsBusiness.length > 0 ? JSON.stringify(result.skillsBusiness) : prev.skillsBusiness,
      requiredSkills: Array.isArray(result.requiredSkills) && result.requiredSkills.length > 0 ? JSON.stringify(result.requiredSkills) : prev.requiredSkills,
      optionalSkills: Array.isArray(result.optionalSkills) && result.optionalSkills.length > 0 ? JSON.stringify(result.optionalSkills) : prev.optionalSkills,
      location: result.location || prev.location,
    }));
    toast({ title: t("partners.missions.toasts.rfpExtracted", "RFP data extracted and pre-filled"), status: "success" });
  };

  const handleRfpConfirm = () => {
    if (rfpConfirmData) applyRfpData(rfpConfirmData);
    setRfpConfirmData(null);
  };

  const handleRfpConfirmCancel = () => {
    setRfpConfirmData(null);
    setRfpFile(null);
  };

  // Purpose change handler — clear non-relevant recipient selections
  const handlePurposeChange = (purpose) => {
    setForm((prev) => {
      const updates = { ...prev, purpose };
      if (purpose === "contact_pool") {
        updates.recipientPartnerCompanyIds = [];
      } else {
        updates.recipientPoolIds = [];
      }
      return updates;
    });
  };

  // Skill tag helpers
  const parseSkillList = (jsonStr) => {
    if (!jsonStr) return [];
    try {
      const parsed = JSON.parse(jsonStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return jsonStr
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  };

  const addSkill = (fieldName, skill) => {
    if (!skill.trim()) return;
    const current = parseSkillList(form[fieldName]);
    if (current.includes(skill.trim())) return;
    const updated = [...current, skill.trim()];
    setForm((prev) => ({ ...prev, [fieldName]: JSON.stringify(updated) }));
  };

  const removeSkill = (fieldName, index) => {
    const current = parseSkillList(form[fieldName]);
    current.splice(index, 1);
    setForm((prev) => ({
      ...prev,
      [fieldName]: current.length ? JSON.stringify(current) : "",
    }));
  };

  const SKILL_CATEGORY_MAP = {
    technical: "skillsTechnical",
    functional: "skillsFunctional",
    business: "skillsBusiness",
  };

  const handleSkillKeyDown = (categoryKey, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const fieldName = SKILL_CATEGORY_MAP[categoryKey];
      addSkill(fieldName, skillInput[categoryKey]);
      setSkillInput((prev) => ({ ...prev, [categoryKey]: "" }));
    }
  };

  const handleSkillInputChange = (categoryKey, value) => {
    setSkillInput((prev) => ({ ...prev, [categoryKey]: value }));
  };

  // Toggle a skill between required / optional / neither
  const getAllCategorySkills = () => {
    const technical = parseSkillList(form.skillsTechnical);
    const functional = parseSkillList(form.skillsFunctional);
    const business = parseSkillList(form.skillsBusiness);
    return [...technical, ...functional, ...business];
  };

  const toggleSkillImportance = (skill, targetField) => {
    const reqList = parseSkillList(form.requiredSkills);
    const optList = parseSkillList(form.optionalSkills);

    // Remove from both first
    const newReq = reqList.filter((s) => s !== skill);
    const newOpt = optList.filter((s) => s !== skill);

    if (targetField === "required" && !reqList.includes(skill)) {
      newReq.push(skill);
    } else if (targetField === "optional" && !optList.includes(skill)) {
      newOpt.push(skill);
    }
    // If targetField === "none", skill is removed from both (already done above)

    setForm((prev) => ({
      ...prev,
      requiredSkills: newReq.length ? JSON.stringify(newReq) : "",
      optionalSkills: newOpt.length ? JSON.stringify(newOpt) : "",
    }));
  };

  const handlePoolToggle = (poolId) => {
    setForm((prev) => {
      const ids = prev.recipientPoolIds.includes(poolId)
        ? prev.recipientPoolIds.filter((id) => id !== poolId)
        : [...prev.recipientPoolIds, poolId];
      return { ...prev, recipientPoolIds: ids };
    });
  };

  const handlePartnerToggle = (companyId) => {
    setForm((prev) => {
      const ids = prev.recipientPartnerCompanyIds.includes(companyId)
        ? prev.recipientPartnerCompanyIds.filter((id) => id !== companyId)
        : [...prev.recipientPartnerCompanyIds, companyId];
      return { ...prev, recipientPartnerCompanyIds: ids };
    });
  };

  // ── AI prose generation (Review step) ───────────────────────────────────
  const runGenerateSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const result = await generateMissionSections(
        {
          title: form.title || undefined,
          description: form.description || undefined,
          skillsTechnical: form.skillsTechnical || undefined,
          skillsFunctional: form.skillsFunctional || undefined,
          skillsBusiness: form.skillsBusiness || undefined,
          requiredSkills: form.requiredSkills || undefined,
          optionalSkills: form.optionalSkills || undefined,
          // Generate in the active portal UI language (English vs French).
          language: (i18n.language || "fr").toLowerCase().startsWith("en") ? "en" : "fr",
        },
        { buId: selectedBusinessUnitId || undefined }
      );
      setForm((prev) => ({
        ...prev,
        generatedSections: {
          responsibilities: result?.responsibilities || "",
          requiredSkills: result?.requiredSkills || "",
          candidateProfile: result?.candidateProfile || "",
        },
      }));
      sectionsEditedRef.current = false;
    } catch (e) {
      toast({
        title: t("partners.missions.toasts.errorTitle", "Error"),
        description: t(
          "partners.missions.sections.generateError",
          "Could not generate the text. Please try again."
        ),
        status: "error",
      });
    } finally {
      setSectionsLoading(false);
    }
  }, [form, selectedBusinessUnitId, toast, t, i18n.language]);

  // Auto-generate prose once when the recruiter first reaches the Review step
  // (only if empty and there is source material to work from). Manual edits are
  // never silently overwritten — regeneration is then explicit (with confirm).
  const REVIEW_STEP_INDEX = STEPS.length - 1;
  useEffect(() => {
    if (!isOpen || activeStep !== REVIEW_STEP_INDEX) return;
    if (sectionsAutoTriedRef.current) return;
    if (!sectionsAreEmpty(form.generatedSections)) return;
    const hasSource =
      form.title ||
      form.description ||
      form.skillsFunctional ||
      form.requiredSkills ||
      form.skillsTechnical ||
      form.skillsBusiness ||
      form.optionalSkills;
    if (!hasSource) return;
    // Mark "tried" only once we actually generate, so a premature/spurious fire
    // (e.g. a stale step on reopen, or no source yet) never blocks a later run.
    sectionsAutoTriedRef.current = true;
    runGenerateSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeStep, form.generatedSections]);

  const updateSectionField = (key, value) => {
    sectionsEditedRef.current = true;
    setForm((prev) => ({
      ...prev,
      generatedSections: { ...prev.generatedSections, [key]: value },
    }));
  };

  // Build the mission API payload from current form state. Shared by submit and
  // by the live email preview so the preview matches what will be created/sent.
  const buildMissionPayload = useCallback(() => {
      return {
        purpose: form.purpose,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        skillsTechnical: form.skillsTechnical || undefined,
        skillsFunctional: form.skillsFunctional || undefined,
        skillsBusiness: form.skillsBusiness || undefined,
        requiredSkills: form.requiredSkills || undefined,
        optionalSkills: form.optionalSkills || undefined,
        location: form.location.trim() || undefined,
        tjmMin: form.tjmSelonProfil
          ? null
          : form.tjmMin
          ? Number(form.tjmMin)
          : undefined,
        tjmMax: form.tjmSelonProfil
          ? null
          : form.tjmMax
          ? Number(form.tjmMax)
          : undefined,
        salaireMin: form.salaireSelonProfil
          ? null
          : form.salaireMin
          ? Number(form.salaireMin)
          : undefined,
        salaireMax: form.salaireSelonProfil
          ? null
          : form.salaireMax
          ? Number(form.salaireMax)
          : undefined,
        tjmSelonProfil: !!form.tjmSelonProfil,
        salaireSelonProfil: !!form.salaireSelonProfil,
        startAsap: !!form.startAsap,
        startDate: !form.startAsap && form.startDate ? form.startDate : undefined,
        durationDays: form.durationUnit === "days" && form.durationDays ? Number(form.durationDays) : undefined,
        durationMonths: form.durationUnit === "months" && form.durationMonths ? Number(form.durationMonths) : undefined,
        durationUnit: form.durationUnit || undefined,
        isRenewable: form.isRenewable || false,
        contractTypes: form.contractTypes.length > 0 ? form.contractTypes : undefined,
        anonymizationConfig:
          form.anonymizationConfig && form.anonymizationConfig !== "{}"
            ? form.anonymizationConfig
            : undefined,
        atsThreshold: form.atsThreshold || "none",
        clientNeedId: form.clientNeedId ? Number(form.clientNeedId) : undefined,
        evaluationConfig: form.evaluationEnabled
          ? JSON.stringify({
              enabled: true,
              auto_mode: form.evaluationAutoMode,
              language: form.evaluationLanguage,
              job_matrix: form.evaluationAutoMode
                ? form.evaluationJobMatrix
                : form.evaluationJobMatrix.filter((s) => s.selected),
            })
          : undefined,
        recipientPoolIds:
          form.recipientPoolIds.length > 0 ? form.recipientPoolIds : undefined,
        recipientPartnerCompanyIds:
          form.recipientPartnerCompanyIds.length > 0
            ? form.recipientPartnerCompanyIds
            : undefined,
        generatedSections: sectionsAreEmpty(form.generatedSections)
          ? undefined
          : JSON.stringify({
              responsibilities: form.generatedSections.responsibilities || "",
              requiredSkills: form.generatedSections.requiredSkills || "",
              candidateProfile: form.generatedSections.candidateProfile || "",
            }),
        // Recruiter overrides: only sent when actually customized. An empty string
        // explicitly clears a previously-stored override (on edit/update).
        customSubject: subjectDirty ? form.customSubject.trim() : undefined,
        customEmailHtml: customizeEmail ? form.customEmailHtml || "" : undefined,
      };
  }, [form, subjectDirty, customizeEmail]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...buildMissionPayload(),
        ...(!isEdit && form.sendDirectly ? { sendDirectly: true } : {}),
      };

      if (isEdit) {
        await updateMission(mission.id, payload, {
          buId: selectedBusinessUnitId || undefined,
        });
        toast({
          title: t("partners.missions.toasts.updated", "Mission updated"),
          status: "success",
        });
      } else {
        await createMission(payload, {
          buId: selectedBusinessUnitId || undefined,
        });
        toast({
          title: t("partners.missions.toasts.created", "Mission created"),
          status: "success",
        });
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: t("partners.missions.toasts.errorTitle", "Error"),
        description:
          e?.response?.data?.detail ||
          t("partners.missions.toasts.errorDescription", "Could not save mission."),
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Live email preview (Review step) ──────────────────────────────────────
  const fetchPreview = useCallback(async () => {
    const seq = ++previewSeqRef.current;
    setPreviewLoading(true);
    setPreviewError(false);
    try {
      const res = await previewMissionEmail(buildMissionPayload(), {
        buId: selectedBusinessUnitId || undefined,
      });
      if (seq === previewSeqRef.current) {
        setPreviewHtml(res?.html || "");
        if (res?.subject !== undefined) setPreviewSubject(res.subject || "");
        if (res?.defaultBodyHtml !== undefined) setDefaultBodyHtml(res.defaultBodyHtml || "");
      }
    } catch {
      if (seq === previewSeqRef.current) setPreviewError(true);
    } finally {
      if (seq === previewSeqRef.current) setPreviewLoading(false);
    }
  }, [buildMissionPayload, selectedBusinessUnitId]);

  // Refresh the preview when entering the Review step and (debounced) whenever
  // an email-affecting field changes, so the recruiter sees edits live.
  useEffect(() => {
    if (!isOpen || activeStep !== REVIEW_STEP_INDEX) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(() => {
      fetchPreview();
    }, 500);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    activeStep,
    form.description,
    form.title,
    form.location,
    form.generatedSections.responsibilities,
    form.generatedSections.requiredSkills,
    form.generatedSections.candidateProfile,
    form.tjmMin,
    form.tjmMax,
    form.salaireMin,
    form.salaireMax,
    form.tjmSelonProfil,
    form.salaireSelonProfil,
    form.startDate,
    form.startAsap,
    form.durationDays,
    form.durationMonths,
    form.durationUnit,
    form.isRenewable,
    form.contractTypes,
    form.customSubject,
    form.customEmailHtml,
    subjectDirty,
    customizeEmail,
  ]);

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!form.purpose;
      case 1:
        return !!form.title.trim();
      case 2:
        return true;
      case 3:
        return true;
      case 4:
        return form.purpose === "contact_pool"
          ? form.recipientPoolIds.length > 0
          : form.recipientPartnerCompanyIds.length > 0;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderPurposeStep();
      case 1:
        return renderDetailsStep();
      case 2:
        return renderSkillsStep();
      case 3:
        return renderConditionsStep();
      case 4:
        return renderRecipientsStep();
      case 5:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderPurposeStep = () => (
    <VStack spacing="16px" align="stretch">
      <HelpText>
        {t(
          "partners.missions.form.purposeDescription",
          "Select the type of communication you want to send."
        )}
      </HelpText>
      <Stack spacing="10px">
        {PURPOSE_OPTIONS.map((opt) => {
          const selected = form.purpose === opt.value;
          return (
            <Box
              key={opt.value}
              p="14px"
              borderWidth="1px"
              borderColor={selected ? "portal.primary" : "portal.line"}
              boxShadow={selected ? "0 0 0 1px var(--chakra-colors-portal-primary)" : "none"}
              borderRadius="10px"
              cursor="pointer"
              bg={selected ? "portal.active" : "portal.panel"}
              onClick={() => handlePurposeChange(opt.value)}
              _hover={{ borderColor: "portal.primary" }}
              transition="all 0.15s"
            >
              <Flex align="center" gap="8px">
                <Text
                  fontSize="13px"
                  fontWeight="700"
                  color="portal.text"
                  sx={{ marginBottom: "0 !important" }}
                >
                  {t(opt.labelKey, opt.label)}
                </Text>
              </Flex>
            </Box>
          );
        })}
      </Stack>
    </VStack>
  );

  const renderDetailsStep = () => (
    <VStack spacing="16px" align="stretch">
      {/* PILOT Integration: pre-fill from ClientNeed */}
      {hasPilot && (
        <FormControl>
          <FieldLabel>
            {t("partners.missions.form.pilotNeed", "Pre-fill from Client Need (PILOT)")}
          </FieldLabel>
          {loadingPilotNeeds ? (
            <HelpText>
              {t("partners.missions.form.loadingNeeds", "Loading client needs...")}
            </HelpText>
          ) : pilotNeeds.length === 0 ? (
            <HelpText>
              {t("partners.missions.form.noNeeds", "No open client needs available.")}
            </HelpText>
          ) : (
            <Select
              placeholder={t("partners.missions.form.selectNeed", "-- Select a client need to pre-fill --")}
              value={form.clientNeedId}
              onChange={(e) => handlePilotNeedSelect(e.target.value)}
              sx={SELECT_SX}
            >
              {pilotNeeds.map((need) => (
                <option key={need.id} value={need.id}>
                  {need.title}
                  {need.clientName ? ` — ${need.clientName}` : ""}
                </option>
              ))}
            </Select>
          )}
        </FormControl>
      )}
      <FormControl isRequired>
        <FieldLabel isRequired>
          {t("partners.missions.form.title", "Title")}
        </FieldLabel>
        <Input
          name="title"
          size="sm"
          value={form.title}
          onChange={handleChange}
          aria-required="true"
          placeholder={t(
            "partners.missions.form.titlePlaceholder",
            "e.g., Développeur Full-Stack React/Python"
          )}
        />
      </FormControl>
      {/* The Description field lives on the final review step (next to the email
          preview) so recruiters edit the message content at the very end, with
          full visibility of what consultants will receive. */}
      {/* RFP Upload */}
      <FormControl>
        <FieldLabel>
          {t("partners.missions.form.rfpUpload", "Upload RFP (optional)")}
        </FieldLabel>
        <HelpText mb="8px">
          {t("partners.missions.form.rfpUploadDescription", "Upload a job description document to auto-fill the form fields. Supported formats: PDF, DOCX (max 10 MB).")}
        </HelpText>
        <Box
          borderWidth="2px"
          borderStyle="dashed"
          borderColor={rfpDragActive ? "portal.primary" : "portal.line"}
          borderRadius="10px"
          p="16px"
          minH="88px"
          bg={rfpDragActive ? "portal.active" : "portal.soft"}
          cursor={rfpLoading ? "wait" : "pointer"}
          role="button"
          tabIndex={0}
          onClick={() => !rfpLoading && rfpInputRef.current?.click()}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !rfpLoading) { e.preventDefault(); rfpInputRef.current?.click(); } }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); rfpDragCounter.current++; setRfpDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); rfpDragCounter.current--; if (rfpDragCounter.current <= 0) { rfpDragCounter.current = 0; setRfpDragActive(false); } }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            rfpDragCounter.current = 0;
            setRfpDragActive(false);
            const file = e.dataTransfer?.files?.[0];
            if (file) handleRfpFile(file);
          }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          transition="all 0.15s"
          _hover={rfpLoading ? {} : { borderColor: "portal.primary", bg: "portal.active" }}
          data-testid="rfp-dropzone"
        >
          {rfpLoading ? (
            <Text fontSize="13px" fontWeight="700" color="portal.primary" sx={{ marginBottom: "0 !important" }}>
              {t("partners.missions.form.rfpExtracting", "Extracting data from document...")}
            </Text>
          ) : rfpFile ? (
            <HStack>
              <Tag colorScheme="blue" size="md" variant="subtle" borderRadius="999px">
                <TagLabel>{rfpFile.name}</TagLabel>
                <TagCloseButton onClick={(e) => { e.stopPropagation(); setRfpFile(null); }} />
              </Tag>
            </HStack>
          ) : (
            <>
              <Text fontSize="13px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                {t("partners.missions.form.rfpDragDrop", "Drag & drop an RFP file here, or click to browse")}
              </Text>
              <Text fontSize="11px" color="portal.muted" mt="4px" sx={{ marginBottom: "0 !important" }}>PDF, DOCX</Text>
            </>
          )}
        </Box>
        <input
          ref={rfpInputRef}
          type="file"
          accept=".pdf,.docx"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleRfpFile(file);
            e.target.value = "";
          }}
          data-testid="rfp-file-input"
        />
      </FormControl>
    </VStack>
  );

  const renderSkillsStep = () => {
    const reqList = parseSkillList(form.requiredSkills);
    const optList = parseSkillList(form.optionalSkills);
    const allSkills = getAllCategorySkills();

    const CATEGORIES = [
      { key: "technical", field: "skillsTechnical", color: "purple",
        label: t("partners.missions.form.technicalSkills", "Technical Skills"),
        placeholder: t("partners.missions.form.technicalSkillsPlaceholder", "e.g., React, Python, PostgreSQL") },
      { key: "functional", field: "skillsFunctional", color: "orange",
        label: t("partners.missions.form.functionalSkills", "Functional Skills"),
        placeholder: t("partners.missions.form.functionalSkillsPlaceholder", "e.g., Agile, Scrum, Project Management") },
      { key: "business", field: "skillsBusiness", color: "green",
        label: t("partners.missions.form.businessSkills", "Business Skills"),
        placeholder: t("partners.missions.form.businessSkillsPlaceholder", "e.g., Finance, Marketing, Sales") },
    ];

    return (
      <VStack spacing="18px" align="stretch">
        <HelpText>
          {t(
            "partners.missions.form.skillsDescription",
            "Add skills by category, then mark each as required or optional for scoring."
          )}
        </HelpText>

        {/* 3 category inputs */}
        {CATEGORIES.map((cat) => (
          <FormControl key={cat.key}>
            <FieldLabel>{cat.label}</FieldLabel>
            <Flex gap="8px">
              <Input
                value={skillInput[cat.key]}
                onChange={(e) => handleSkillInputChange(cat.key, e.target.value)}
                onKeyDown={(e) => handleSkillKeyDown(cat.key, e)}
                placeholder={cat.placeholder}
                size="sm"
              />
              <Button
                size="sm"
                colorScheme={cat.color}
                variant="outline"
                onClick={() => {
                  addSkill(cat.field, skillInput[cat.key]);
                  setSkillInput((prev) => ({ ...prev, [cat.key]: "" }));
                }}
                isDisabled={!skillInput[cat.key]?.trim()}
                flexShrink={0}
              >
                {t("partners.missions.form.addSkillButton", "Add")}
              </Button>
            </Flex>
            <Flex mt="8px" gap="6px" flexWrap="wrap">
              {parseSkillList(form[cat.field]).map((skill, i) => (
                <Tag key={i} size="sm" colorScheme={cat.color} variant="solid" borderRadius="999px">
                  <TagLabel>{skill}</TagLabel>
                  <TagCloseButton onClick={() => removeSkill(cat.field, i)} />
                </Tag>
              ))}
            </Flex>
          </FormControl>
        ))}

        {/* Required / Optional classification */}
        {allSkills.length > 0 && (
          <TokenBlock>
            <SectionTitle>
              {t("partners.missions.form.importanceTitle", "Skill Importance")}
            </SectionTitle>
            <HelpText mt="4px" mb="12px">
              {t("partners.missions.form.importanceDescription", "Click to toggle each skill as required, optional, or unclassified. Required skills weigh 80% in scoring, optional 20%.")}
            </HelpText>
            <Flex gap="6px" flexWrap="wrap">
              {allSkills.map((skill, i) => {
                const isReq = reqList.includes(skill);
                const isOpt = optList.includes(skill);
                let colorScheme = "gray";
                let variant = "outline";
                let label = skill;
                if (isReq) { colorScheme = "green"; variant = "solid"; }
                else if (isOpt) { colorScheme = "blue"; variant = "subtle"; }

                return (
                  <Tag
                    key={`${skill}-${i}`}
                    size="sm"
                    colorScheme={colorScheme}
                    variant={variant}
                    borderRadius="999px"
                    cursor="pointer"
                    onClick={() => {
                      if (isReq) toggleSkillImportance(skill, "optional");
                      else if (isOpt) toggleSkillImportance(skill, "none");
                      else toggleSkillImportance(skill, "required");
                    }}
                  >
                    <TagLabel>{label}</TagLabel>
                  </Tag>
                );
              })}
            </Flex>
            <Flex mt="12px" gap="16px" fontSize="11px" color="portal.muted" flexWrap="wrap">
              <Flex align="center" gap="6px">
                <Box w="10px" h="10px" borderRadius="3px" bg="green.500" />
                <Text sx={{ marginBottom: "0 !important" }}>{t("partners.missions.form.legendRequired", "Required")}</Text>
              </Flex>
              <Flex align="center" gap="6px">
                <Box w="10px" h="10px" borderRadius="3px" bg="blue.100" />
                <Text sx={{ marginBottom: "0 !important" }}>{t("partners.missions.form.legendOptional", "Optional")}</Text>
              </Flex>
              <Flex align="center" gap="6px">
                <Box w="10px" h="10px" borderRadius="3px" bg="portal.soft" borderWidth="1px" borderColor="portal.line" />
                <Text sx={{ marginBottom: "0 !important" }}>{t("partners.missions.form.legendUnclassified", "Unclassified")}</Text>
              </Flex>
            </Flex>
          </TokenBlock>
        )}

        {/* SKILLS Evaluation Configuration */}
        {hasSkills && allSkills.length > 0 && (
          <TokenBlock>
            <FormControl display="flex" alignItems="center" mb={form.evaluationEnabled ? "12px" : "0"}>
              <Switch
                id="evaluationEnabled"
                isChecked={form.evaluationEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setForm((prev) => {
                    if (enabled && prev.evaluationJobMatrix.length === 0) {
                      const matrix = [];
                      const addSkills = (jsonStr, type) => {
                        try {
                          const arr = JSON.parse(jsonStr || "[]");
                          if (Array.isArray(arr)) {
                            arr.forEach((s) => matrix.push({
                              technology: s,
                              difficulty_level: "Confirmé",
                              importance: "primary",
                              type,
                              technical_subcategory: type === "technique" ? "dev" : "",
                              selected: false,
                            }));
                          }
                        } catch { /* ignore */ }
                      };
                      addSkills(prev.skillsTechnical, "technique");
                      addSkills(prev.skillsFunctional, "fonctionnel");
                      addSkills(prev.skillsBusiness, "metier");
                      return { ...prev, evaluationEnabled: true, evaluationJobMatrix: matrix };
                    }
                    return { ...prev, evaluationEnabled: enabled };
                  });
                }}
                colorScheme="green"
                mr="12px"
              />
              <Text
                as="label"
                htmlFor="evaluationEnabled"
                fontSize="13px"
                fontWeight="800"
                color="portal.text"
                sx={{ marginBottom: "0 !important" }}
              >
                {t("partners.missions.form.includeEvaluation", "Include SKILLS evaluation")}
              </Text>
            </FormControl>

            {form.evaluationEnabled && (
              <VStack spacing="12px" align="stretch">
                <HelpText>
                  {t(
                    "partners.missions.form.evaluationDescription",
                    "Candidates will receive a separate evaluation email with a technical test. Select which skills to include (max 5)."
                  )}
                </HelpText>

                <Flex gap="16px" align="flex-end" flexWrap="wrap">
                  <FormControl w="200px">
                    <FieldLabel>
                      {t("partners.missions.form.evaluationLanguage", "Evaluation Language")}
                    </FieldLabel>
                    <Select
                      value={form.evaluationLanguage}
                      onChange={(e) => setForm((prev) => ({ ...prev, evaluationLanguage: e.target.value }))}
                      sx={SELECT_SX}
                    >
                      {EVALUATION_LANGUAGES.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl display="flex" alignItems="center" w="auto" pb="4px">
                    <Switch
                      id="evaluationAutoMode"
                      isChecked={form.evaluationAutoMode}
                      onChange={(e) => setForm((prev) => ({ ...prev, evaluationAutoMode: e.target.checked }))}
                      colorScheme="blue"
                      size="sm"
                      mr="8px"
                    />
                    <Text
                      as="label"
                      htmlFor="evaluationAutoMode"
                      fontSize="13px"
                      fontWeight="600"
                      color="portal.text"
                      whiteSpace="nowrap"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {t("partners.missions.form.autoMode", "Auto mode")}
                    </Text>
                  </FormControl>
                </Flex>

                {form.evaluationAutoMode ? (
                  <Box p="12px" bg="portal.panel" borderRadius="10px" borderLeftWidth="4px" borderLeftColor="portal.primary" borderWidth="1px" borderColor="portal.line">
                    <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t(
                        "partners.missions.form.autoModeDescription",
                        "Auto mode: the system will automatically select the most relevant skills and generate the evaluation. All skills below will be sent for analysis."
                      )}
                    </Text>
                  </Box>
                ) : (
                  <Flex align="center" gap="8px">
                    <Text fontSize="12px" fontWeight="700" color={form.evaluationJobMatrix.filter((s) => s.selected).length > MAX_EVAL_SKILLS ? "portal.danger" : "portal.muted"} sx={{ marginBottom: "0 !important" }}>
                      {t("partners.missions.form.selectedSkillsCount", "Selected: {{count}} / {{max}}", {
                        count: form.evaluationJobMatrix.filter((s) => s.selected).length,
                        max: MAX_EVAL_SKILLS,
                      })}
                    </Text>
                    {form.evaluationJobMatrix.filter((s) => s.selected).length > MAX_EVAL_SKILLS && (
                      <Text fontSize="12px" color="portal.danger" fontWeight="800" sx={{ marginBottom: "0 !important" }}>
                        {t("partners.missions.form.tooManySkills", "Maximum {{max}} skills allowed", { max: MAX_EVAL_SKILLS })}
                      </Text>
                    )}
                  </Flex>
                )}

                <Box overflowX="auto" borderWidth="1px" borderColor="portal.line" borderRadius="10px" bg="portal.panel">
                  <Table
                    size="sm"
                    variant="simple"
                    sx={{
                      fontSize: "12px",
                      "& thead th": { height: "34px", py: 0, fontSize: "10.5px", bg: "portal.soft", borderColor: "portal.line" },
                      "& tbody td": { py: "6px", borderColor: "#edf2f5" },
                    }}
                  >
                    <Thead>
                      <Tr>
                        {!form.evaluationAutoMode && <Th w="40px"></Th>}
                        <Th>{t("partners.missions.form.evalSkill", "Skill")}</Th>
                        <Th>{t("partners.missions.form.evalType", "Type")}</Th>
                        <Th>{t("partners.missions.form.evalDifficulty", "Difficulty")}</Th>
                        <Th>{t("partners.missions.form.evalImportance", "Importance")}</Th>
                        <Th>{t("partners.missions.form.evalCoding", "Coding")}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {form.evaluationJobMatrix.map((skill, idx) => {
                        const selectedCount = form.evaluationJobMatrix.filter((s) => s.selected).length;
                        const isDisabled = !form.evaluationAutoMode && !skill.selected && selectedCount >= MAX_EVAL_SKILLS;
                        const isRowActive = form.evaluationAutoMode || skill.selected;

                        return (
                          <Tr key={idx} opacity={isRowActive ? 1 : 0.5}>
                            {!form.evaluationAutoMode && (
                              <Td>
                                <Checkbox
                                  isChecked={!!skill.selected}
                                  isDisabled={isDisabled}
                                  onChange={(e) => {
                                    const updated = [...form.evaluationJobMatrix];
                                    updated[idx] = { ...updated[idx], selected: e.target.checked };
                                    setForm((prev) => ({ ...prev, evaluationJobMatrix: updated }));
                                  }}
                                  colorScheme="green"
                                  size="sm"
                                />
                              </Td>
                            )}
                            <Td fontWeight="700" fontSize="12px" color="portal.text">{skill.technology}</Td>
                            <Td>
                              <Badge size="sm" colorScheme={skill.type === "technique" ? "purple" : skill.type === "fonctionnel" ? "orange" : "green"} variant="subtle">
                                {skill.type}
                              </Badge>
                            </Td>
                            <Td>
                              <Select size="xs" value={skill.difficulty_level}
                                onChange={(e) => { const u = [...form.evaluationJobMatrix]; u[idx] = { ...u[idx], difficulty_level: e.target.value }; setForm((p) => ({ ...p, evaluationJobMatrix: u })); }}
                                sx={{ paddingTop: "0 !important", paddingBottom: "0 !important", lineHeight: "24px !important", height: "24px !important" }}>
                                {DIFFICULTY_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                              </Select>
                            </Td>
                            <Td>
                              <Select size="xs" value={skill.importance}
                                onChange={(e) => { const u = [...form.evaluationJobMatrix]; u[idx] = { ...u[idx], importance: e.target.value }; setForm((p) => ({ ...p, evaluationJobMatrix: u })); }}
                                sx={{ paddingTop: "0 !important", paddingBottom: "0 !important", lineHeight: "24px !important", height: "24px !important" }}>
                                {IMPORTANCE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </Select>
                            </Td>
                            <Td>
                              {skill.type === "technique" ? (
                                <Select size="xs" value={skill.technical_subcategory || "non-dev"}
                                  onChange={(e) => { const u = [...form.evaluationJobMatrix]; u[idx] = { ...u[idx], technical_subcategory: e.target.value }; setForm((p) => ({ ...p, evaluationJobMatrix: u })); }}
                                  sx={{ paddingTop: "0 !important", paddingBottom: "0 !important", lineHeight: "24px !important", height: "24px !important" }}>
                                  <option value="dev">Dev (coding)</option>
                                  <option value="non-dev">Non-dev</option>
                                </Select>
                              ) : (
                                <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>-</Text>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            )}
          </TokenBlock>
        )}
      </VStack>
    );
  };

  const hasTjmContract = form.contractTypes.some((c) => TJM_CONTRACT_TYPES.includes(c));
  const hasCdiContract = form.contractTypes.includes("cdi");

  const handleContractTypeToggle = (value) => {
    setForm((prev) => {
      const selected = prev.contractTypes.includes(value)
        ? prev.contractTypes.filter((c) => c !== value)
        : [...prev.contractTypes, value];
      const nextHasTjm = selected.some((c) => TJM_CONTRACT_TYPES.includes(c));
      const nextHasCdi = selected.includes("cdi");
      return {
        ...prev,
        contractTypes: selected,
        // Clear a rate group's values when no selected type uses it anymore.
        tjmMin: nextHasTjm ? prev.tjmMin : "",
        tjmMax: nextHasTjm ? prev.tjmMax : "",
        tjmSelonProfil: nextHasTjm ? prev.tjmSelonProfil : false,
        salaireMin: nextHasCdi ? prev.salaireMin : "",
        salaireMax: nextHasCdi ? prev.salaireMax : "",
        salaireSelonProfil: nextHasCdi ? prev.salaireSelonProfil : false,
      };
    });
  };

  const renderConditionsStep = () => (
    <VStack spacing="16px" align="stretch">
      {/* Type de contrat — first field on step 4. Multi-select: a mission can
          target several contract types at once. */}
      <FormControl>
        <FieldLabel>{t("partners.missions.form.contractType", "Contract Type")}</FieldLabel>
        <HStack spacing="16px" wrap="wrap" data-testid="mission-form-contract-type">
          {CONTRACT_TYPE_OPTIONS.map((opt) => (
            <Checkbox
              key={opt.value}
              isChecked={form.contractTypes.includes(opt.value)}
              onChange={() => handleContractTypeToggle(opt.value)}
              colorScheme="blue"
              data-testid={`mission-form-contract-type-${opt.value}`}
            >
              <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                {t(opt.labelKey, opt.label)}
              </Text>
            </Checkbox>
          ))}
        </HStack>
      </FormControl>

      {/* Conditional rate group */}
      {hasTjmContract && (
        <TokenBlock data-testid="mission-form-tjm-group">
          <Checkbox
            isChecked={!!form.tjmSelonProfil}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                tjmSelonProfil: e.target.checked,
                tjmMin: e.target.checked ? "" : prev.tjmMin,
                tjmMax: e.target.checked ? "" : prev.tjmMax,
              }))
            }
            colorScheme="blue"
            mb="12px"
            data-testid="mission-form-tjm-selon-profil"
          >
            <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
              {t("partners.missions.form.selonProfil", "Selon profil")}
            </Text>
          </Checkbox>
          {!form.tjmSelonProfil && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
              <FormControl>
                <FieldLabel>{t("partners.missions.form.tjmMin", "TJM Min (€)")}</FieldLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  value={form.tjmMin}
                  onChange={(val) => handleNumberChange("tjmMin", val)}
                >
                  <NumberInputField data-testid="mission-form-tjm-min" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FieldLabel>{t("partners.missions.form.tjmMax", "TJM Max (€)")}</FieldLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  value={form.tjmMax}
                  onChange={(val) => handleNumberChange("tjmMax", val)}
                >
                  <NumberInputField data-testid="mission-form-tjm-max" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
          )}
        </TokenBlock>
      )}

      {hasCdiContract && (
        <TokenBlock data-testid="mission-form-salaire-group">
          <Checkbox
            isChecked={!!form.salaireSelonProfil}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                salaireSelonProfil: e.target.checked,
                salaireMin: e.target.checked ? "" : prev.salaireMin,
                salaireMax: e.target.checked ? "" : prev.salaireMax,
              }))
            }
            colorScheme="blue"
            mb="12px"
            data-testid="mission-form-salaire-selon-profil"
          >
            <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
              {t("partners.missions.form.selonProfil", "Selon profil")}
            </Text>
          </Checkbox>
          {!form.salaireSelonProfil && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
              <FormControl>
                <FieldLabel>{t("partners.missions.form.salaireMin", "Salary Min (€/year)")}</FieldLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  value={form.salaireMin}
                  onChange={(val) => handleNumberChange("salaireMin", val)}
                >
                  <NumberInputField data-testid="mission-form-salaire-min" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <FormControl>
                <FieldLabel>{t("partners.missions.form.salaireMax", "Salary Max (€/year)")}</FieldLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  value={form.salaireMax}
                  onChange={(val) => handleNumberChange("salaireMax", val)}
                >
                  <NumberInputField data-testid="mission-form-salaire-max" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
          )}
        </TokenBlock>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
        <FormControl>
          <FieldLabel>{t("partners.missions.form.location", "Location")}</FieldLabel>
          <Input
            name="location"
            size="sm"
            value={form.location}
            onChange={handleChange}
            placeholder={t("partners.missions.form.locationPlaceholder", "e.g., Paris, Remote")}
          />
        </FormControl>
        <FormControl>
          <FieldLabel>{t("partners.missions.form.startDate", "Start Date")}</FieldLabel>
          <HStack spacing="12px">
            <Input
              name="startDate"
              size="sm"
              type="date"
              flex={1}
              value={form.startDate}
              onChange={handleChange}
              isDisabled={!!form.startAsap}
              data-testid="mission-form-start-date"
            />
            <Checkbox
              isChecked={!!form.startAsap}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  startAsap: e.target.checked,
                  startDate: e.target.checked ? "" : prev.startDate,
                }))
              }
              colorScheme="blue"
              flexShrink={0}
              data-testid="mission-form-start-asap"
            >
              <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                {t("partners.missions.form.startAsap", "ASAP")}
              </Text>
            </Checkbox>
          </HStack>
        </FormControl>
        <FormControl>
          <FieldLabel>{t("partners.missions.form.durationValue", "Duration")}</FieldLabel>
          <HStack spacing="8px">
            <NumberInput
              size="sm"
              min={1}
              flex={1}
              value={form.durationUnit === "months" ? form.durationMonths : form.durationDays}
              onChange={(val) => {
                if (form.durationUnit === "months") {
                  handleNumberChange("durationMonths", val);
                } else {
                  handleNumberChange("durationDays", val);
                }
              }}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select
              w="140px"
              value={form.durationUnit}
              onChange={(e) => {
                const newUnit = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  durationUnit: newUnit,
                  durationDays: newUnit === "days" ? prev.durationDays : "",
                  durationMonths: newUnit === "months" ? prev.durationMonths : "",
                }));
              }}
              sx={SELECT_SX}
            >
              <option value="days">{t("partners.missions.form.unitDays", "Days")}</option>
              <option value="months">{t("partners.missions.form.unitMonths", "Months")}</option>
            </Select>
          </HStack>
        </FormControl>
        <FormControl display="flex" flexDirection="column">
          <FieldLabel opacity={0} userSelect="none" aria-hidden="true">&nbsp;</FieldLabel>
          <Box display="flex" alignItems="center" h="31px">
            <Checkbox
              isChecked={form.isRenewable}
              onChange={(e) => setForm((prev) => ({ ...prev, isRenewable: e.target.checked }))}
              colorScheme="blue"
            >
              <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                {t("partners.missions.form.isRenewable", "Renewable")}
              </Text>
            </Checkbox>
          </Box>
        </FormControl>
        <FormControl>
          <FieldLabel>{t("partners.missions.form.atsThreshold", "ATS Score Threshold")}</FieldLabel>
          <Select
            name="atsThreshold"
            value={form.atsThreshold}
            onChange={handleChange}
            sx={SELECT_SX}
          >
            <option value="none">{t("partners.missions.form.atsNone", "No threshold")}</option>
            <option value="50">{t("partners.missions.form.ats50", "50% minimum")}</option>
            <option value="60">{t("partners.missions.form.ats60", "60% minimum")}</option>
            <option value="75">{t("partners.missions.form.ats75", "75% minimum")}</option>
            <option value="80">{t("partners.missions.form.ats80", "80% minimum")}</option>
            <option value="85">{t("partners.missions.form.ats85", "85% minimum")}</option>
          </Select>
        </FormControl>
      </SimpleGrid>
    </VStack>
  );

  const renderRecipientsStep = () => {
    const showPools = form.purpose === "contact_pool";
    const showPartners = form.purpose !== "contact_pool";

    return (
      <VStack spacing="18px" align="stretch">
        <HelpText>
          {showPools
            ? t("partners.missions.form.recipientsDescriptionPools", "Select consultant pools that will receive this job posting.")
            : t("partners.missions.form.recipientsDescriptionPartners", "Select partner companies that will receive this mission.")}
        </HelpText>

        {/* Consultant Pools — shown only for contact_pool purpose */}
        {showPools && (
          <Box>
            <SectionTitle mb="8px">
              {t("partners.missions.form.poolsSection", "Consultant Pools")}
            </SectionTitle>
            {loadingPools ? (
              <HelpText>
                {t("partners.missions.form.loadingPools", "Loading pools...")}
              </HelpText>
            ) : pools.length === 0 ? (
              <HelpText>
                {t("partners.missions.form.noPools", "No pools available. Create a pool first.")}
              </HelpText>
            ) : (
              <Stack spacing="8px">
                {pools.map((pool) => {
                  const selected = form.recipientPoolIds.includes(pool.id);
                  return (
                    <Box
                      key={pool.id}
                      p="12px"
                      borderWidth="1px"
                      borderColor={selected ? "portal.primary" : "portal.line"}
                      boxShadow={selected ? "0 0 0 1px var(--chakra-colors-portal-primary)" : "none"}
                      borderRadius="10px"
                      cursor="pointer"
                      bg={selected ? "portal.active" : "portal.panel"}
                      onClick={() => handlePoolToggle(pool.id)}
                      _hover={{ borderColor: "portal.primary" }}
                      transition="all 0.15s"
                    >
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Text fontWeight="700" fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>{pool.name}</Text>
                          <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                            {pool.code} · {pool.memberCount ?? 0} {t("partners.missions.form.members", "members")}
                          </Text>
                        </Box>
                        <Checkbox
                          isChecked={selected}
                          onChange={() => handlePoolToggle(pool.id)}
                          colorScheme="green"
                        />
                      </Flex>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {/* Partner Companies — shown only for propose_assignment */}
        {showPartners && (
          <Box>
            <SectionTitle mb="8px">
              {t("partners.missions.form.partnersSection", "Partner Companies")}
            </SectionTitle>
            {loadingPartners ? (
              <HelpText>
                {t("partners.missions.form.loadingPartners", "Loading partner companies...")}
              </HelpText>
            ) : partnerLinks.length === 0 ? (
              <HelpText>
                {t("partners.missions.form.noPartners", "No partner companies linked yet. Add partners from the Partners page.")}
              </HelpText>
            ) : (
              <Stack spacing="8px">
                {partnerLinks.map((link) => {
                  const selected = form.recipientPartnerCompanyIds.includes(link.partnerCompanyId);
                  return (
                    <Box
                      key={link.partnerCompanyId}
                      p="12px"
                      borderWidth="1px"
                      borderColor={selected ? "portal.primary" : "portal.line"}
                      boxShadow={selected ? "0 0 0 1px var(--chakra-colors-portal-primary)" : "none"}
                      borderRadius="10px"
                      cursor="pointer"
                      bg={selected ? "portal.active" : "portal.panel"}
                      onClick={() => handlePartnerToggle(link.partnerCompanyId)}
                      _hover={{ borderColor: "portal.primary" }}
                      transition="all 0.15s"
                    >
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Text fontWeight="700" fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {link.partnerCompanyName || `Company #${link.partnerCompanyId}`}
                          </Text>
                          <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>{link.partnerCode}</Text>
                        </Box>
                        <Checkbox
                          isChecked={selected}
                          onChange={() => handlePartnerToggle(link.partnerCompanyId)}
                          colorScheme="blue"
                        />
                      </Flex>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}
      </VStack>
    );
  };

  const renderReviewStep = () => {
    const selectedPools = pools.filter((p) =>
      form.recipientPoolIds.includes(p.id)
    );
    const selectedPartners = partnerLinks.filter((l) =>
      form.recipientPartnerCompanyIds.includes(l.partnerCompanyId)
    );
    const techSkills = parseSkillList(form.skillsTechnical);
    const funcSkills = parseSkillList(form.skillsFunctional);
    const busSkills = parseSkillList(form.skillsBusiness);
    const reqSkills = parseSkillList(form.requiredSkills);
    const optSkills = parseSkillList(form.optionalSkills);

    return (
      <Flex direction={{ base: "column", xl: "row" }} gap="16px" align="stretch">
      <VStack spacing="16px" align="stretch" flex={{ xl: "1 1 0" }} minW={0}>
        <HelpText>
          {t(
            "partners.missions.form.reviewDescription",
            "Review your mission before saving."
          )}
        </HelpText>

        <TokenBlock bg="portal.panel">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
            <Box minW={0}>
              <FieldLabel>{t("partners.missions.form.purpose", "Purpose")}</FieldLabel>
              <Badge colorScheme="blue" whiteSpace="normal" wordBreak="break-word" maxW="100%">
                {(() => {
                  const opt = PURPOSE_OPTIONS.find((o) => o.value === form.purpose);
                  return opt ? t(opt.labelKey, opt.label) : form.purpose;
                })()}
              </Badge>
            </Box>
            <Box minW={0}>
              <FieldLabel>{t("partners.missions.form.title", "Title")}</FieldLabel>
              <Text fontSize="13px" fontWeight="600" color="portal.text" wordBreak="break-word" sx={{ marginBottom: "0 !important" }}>{form.title || "-"}</Text>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px" mt="12px">
            {form.location && (
              <Box>
                <FieldLabel>{t("partners.missions.form.location", "Location")}</FieldLabel>
                <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>{form.location}</Text>
              </Box>
            )}
            {(() => {
              const formatRange = (selonProfil, min, max) => {
                if (selonProfil) return t("partners.missions.form.selonProfil", "Based on profile");
                if (min && max) return `${min}€ - ${max}€`;
                if (min) return `${min}€+`;
                if (max) return `≤${max}€`;
                return null;
              };
              const tjmValue = hasTjmContract
                ? formatRange(form.tjmSelonProfil, form.tjmMin, form.tjmMax)
                : null;
              const salaryValue = hasCdiContract
                ? formatRange(form.salaireSelonProfil, form.salaireMin, form.salaireMax)
                : null;
              return (
                <>
                  {tjmValue && (
                    <Box>
                      <FieldLabel>{t("partners.missions.form.tjm", "TJM")}</FieldLabel>
                      <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>{tjmValue}</Text>
                    </Box>
                  )}
                  {salaryValue && (
                    <Box>
                      <FieldLabel>{t("partners.missions.form.salary", "Salary")}</FieldLabel>
                      <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>{salaryValue}</Text>
                    </Box>
                  )}
                </>
              );
            })()}
            {(form.durationDays || form.durationMonths) && (
              <Box>
                <FieldLabel>{t("partners.missions.form.durationValue", "Duration")}</FieldLabel>
                <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                  {form.durationUnit === "months"
                    ? `${form.durationMonths} ${t("partners.missions.form.months", "months")}`
                    : `${form.durationDays} ${t("partners.missions.form.days", "days")}`}
                  {form.isRenewable && ` ${t("partners.missions.form.renewable", "(renewable)")}`}
                </Text>
              </Box>
            )}
            {form.contractTypes.length > 0 && (
              <Box>
                <FieldLabel>{t("partners.missions.form.contractType", "Contract Type")}</FieldLabel>
                <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                  {form.contractTypes
                    .map((c) => {
                      const opt = CONTRACT_TYPE_OPTIONS.find((o) => o.value === c);
                      return opt ? t(opt.labelKey, opt.label) : c;
                    })
                    .join(", ")}
                </Text>
              </Box>
            )}
          </SimpleGrid>
        </TokenBlock>

        {/* Email subject — editable; defaults to the generated subject. */}
        <TokenBlock bg="portal.panel">
          <SectionTitle>
            {t("partners.missions.sections.subjectLabel", "Email subject")}
          </SectionTitle>
          <HelpText mt="4px" mb="8px">
            {t(
              "partners.missions.sections.subjectHelp",
              "The subject line consultants will see. Edit it freely (for example, to flag an update)."
            )}
          </HelpText>
          <Input
            size="sm"
            value={subjectDirty ? form.customSubject : previewSubject}
            onChange={(e) => {
              setSubjectDirty(true);
              setForm((prev) => ({ ...prev, customSubject: e.target.value }));
            }}
            data-testid="email-subject-input"
            placeholder={previewSubject}
          />
          {subjectDirty && (
            <Button
              size="xs"
              variant="link"
              mt="6px"
              onClick={() => {
                setSubjectDirty(false);
                setForm((prev) => ({ ...prev, customSubject: "" }));
              }}
              data-testid="email-subject-reset"
            >
              {t("partners.missions.sections.resetToGenerated", "Reset to generated")}
            </Button>
          )}
        </TokenBlock>

        {/* Customize the full email — opt-in free-form editing of the body. */}
        <Box p="14px" bg="portal.active" borderRadius="10px" borderWidth="1px" borderColor="portal.line">
          <Flex justify="space-between" align="center" gap="10px">
            <Box>
              <SectionTitle>
                {t("partners.missions.sections.customizeTitle", "Customize the full email")}
              </SectionTitle>
              <HelpText mt="4px">
                {t(
                  "partners.missions.sections.customizeHelp",
                  "Turn this on to rewrite the entire email freely. The generated content is the starting point. [Prénom] and the apply link are filled in automatically per consultant."
                )}
              </HelpText>
            </Box>
            <Switch
              colorScheme="green"
              isChecked={customizeEmail}
              isDisabled={!defaultBodyHtml && !form.customEmailHtml}
              onChange={(e) => {
                const on = e.target.checked;
                setCustomizeEmail(on);
                if (on) {
                  setForm((prev) => ({
                    ...prev,
                    customEmailHtml: prev.customEmailHtml || defaultBodyHtml,
                  }));
                } else {
                  setForm((prev) => ({ ...prev, customEmailHtml: "" }));
                }
              }}
              data-testid="customize-email-switch"
            />
          </Flex>
        </Box>

        {customizeEmail && (
          <TokenBlock bg="portal.panel">
            <Flex justify="space-between" align="center" mb="8px" gap="8px">
              <SectionTitle>
                {t("partners.missions.sections.emailBodyLabel", "Email content")}
              </SectionTitle>
              <Button
                size="xs"
                variant="outline"
                flexShrink={0}
                onClick={() => setForm((prev) => ({ ...prev, customEmailHtml: defaultBodyHtml }))}
                data-testid="revert-email-body"
              >
                {t("partners.missions.sections.revertToGenerated", "Revert to generated content")}
              </Button>
            </Flex>
            <RichTextEditor
              value={form.customEmailHtml}
              onChange={(html) => setForm((prev) => ({ ...prev, customEmailHtml: html }))}
              ariaLabel={t("partners.missions.sections.emailBodyLabel", "Email content")}
              data-testid="email-body-editor"
            />
          </TokenBlock>
        )}

        {!customizeEmail && (
          <>
        {/* Editable Description (Contexte) — moved here from the early step so it
            is edited at the very end, next to the email preview. Feeds the email
            "Contexte" section and the intro line. */}
        <TokenBlock bg="portal.panel">
          <SectionTitle>
            {t("partners.missions.sections.descriptionLabel", "Description (Context)")}
          </SectionTitle>
          <HelpText mt="4px" mb="8px">
            {t(
              "partners.missions.sections.descriptionHelp",
              "Shown as the Contexte section of the email. Edit it here before sending."
            )}
          </HelpText>
          <Textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={4}
            fontSize="13px"
            data-testid="review-description"
            placeholder={t(
              "partners.missions.sections.descriptionPlaceholder",
              "Describe the mission, context, and expectations..."
            )}
          />
        </TokenBlock>

        {/* Published text — the prose shown in the email & mission page */}
        <TokenBlock bg="portal.panel">
          <Box mb="10px">
            <SectionTitle>
              {t("partners.missions.sections.title", "Published text")}
            </SectionTitle>
            <HelpText mt="4px">
              {t(
                "partners.missions.sections.help",
                "This wording is published in the email and on the mission page. Edit it freely, you have full control."
              )}
            </HelpText>
          </Box>
          <VStack spacing="12px" align="stretch" position="relative">
            <Box>
              <FieldLabel>
                {t("partners.missions.sections.responsibilities", "Missions, Roles & Responsibilities")}
              </FieldLabel>
              <Textarea
                value={form.generatedSections.responsibilities}
                onChange={(e) => updateSectionField("responsibilities", e.target.value)}
                rows={5}
                fontSize="13px"
                data-testid="section-responsibilities"
                placeholder={t(
                  "partners.missions.sections.responsibilitiesPlaceholder",
                  "Intro sentence followed by '- ' bullet points..."
                )}
              />
            </Box>
            <Box>
              <FieldLabel>
                {t("partners.missions.sections.requiredSkills", "Required / Expected Skills")}
              </FieldLabel>
              <Textarea
                value={form.generatedSections.requiredSkills}
                onChange={(e) => updateSectionField("requiredSkills", e.target.value)}
                rows={5}
                fontSize="13px"
                data-testid="section-required-skills"
                placeholder={t(
                  "partners.missions.sections.requiredSkillsPlaceholder",
                  "Intro sentence followed by '- ' bullet points..."
                )}
              />
            </Box>
            <Box>
              <FieldLabel>
                {t("partners.missions.sections.candidateProfile", "Candidate Profile")}
              </FieldLabel>
              <Textarea
                value={form.generatedSections.candidateProfile}
                onChange={(e) => updateSectionField("candidateProfile", e.target.value)}
                rows={5}
                fontSize="13px"
                data-testid="section-candidate-profile"
                placeholder={t(
                  "partners.missions.sections.candidateProfilePlaceholder",
                  "Intro sentence followed by '- ' bullet points..."
                )}
              />
            </Box>
            {sectionsLoading && (
              <Flex
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                align="center"
                justify="center"
                bg="rgba(255,255,255,0.72)"
                borderRadius="8px"
                zIndex={2}
                data-testid="sections-loading"
              >
                <HStack spacing="8px">
                  <Spinner size="sm" color="portal.primary" />
                  <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                    {t("partners.missions.sections.generating", "Generating...")}
                  </Text>
                </HStack>
              </Flex>
            )}
          </VStack>
        </TokenBlock>
          </>
        )}

        {techSkills.length > 0 && (
          <Box>
            <FieldLabel>{t("partners.missions.form.technicalSkills", "Technical Skills")}</FieldLabel>
            <Flex gap="6px" flexWrap="wrap">
              {techSkills.map((s, i) => (
                <Tag key={i} colorScheme="purple" size="sm" variant="solid" borderRadius="999px">{s}</Tag>
              ))}
            </Flex>
          </Box>
        )}

        {funcSkills.length > 0 && (
          <Box>
            <FieldLabel>{t("partners.missions.form.functionalSkills", "Functional Skills")}</FieldLabel>
            <Flex gap="6px" flexWrap="wrap">
              {funcSkills.map((s, i) => (
                <Tag key={i} colorScheme="orange" size="sm" variant="solid" borderRadius="999px">{s}</Tag>
              ))}
            </Flex>
          </Box>
        )}

        {busSkills.length > 0 && (
          <Box>
            <FieldLabel>{t("partners.missions.form.businessSkills", "Business Skills")}</FieldLabel>
            <Flex gap="6px" flexWrap="wrap">
              {busSkills.map((s, i) => (
                <Tag key={i} colorScheme="green" size="sm" variant="solid" borderRadius="999px">{s}</Tag>
              ))}
            </Flex>
          </Box>
        )}

        {reqSkills.length > 0 && (
          <Box>
            <FieldLabel>{t("partners.missions.form.requiredSkills", "Required Skills")}</FieldLabel>
            <Flex gap="6px" flexWrap="wrap">
              {reqSkills.map((s, i) => (
                <Tag key={i} colorScheme="green" size="sm" borderRadius="999px">{s}</Tag>
              ))}
            </Flex>
          </Box>
        )}

        {optSkills.length > 0 && (
          <Box>
            <FieldLabel>{t("partners.missions.form.optionalSkills", "Optional Skills")}</FieldLabel>
            <Flex gap="6px" flexWrap="wrap">
              {optSkills.map((s, i) => (
                <Tag key={i} colorScheme="blue" size="sm" borderRadius="999px">{s}</Tag>
              ))}
            </Flex>
          </Box>
        )}

        {form.evaluationEnabled && form.evaluationJobMatrix.length > 0 && (() => {
          const displaySkills = form.evaluationAutoMode
            ? form.evaluationJobMatrix
            : form.evaluationJobMatrix.filter((s) => s.selected);
          return (
            <Box>
              <FieldLabel>{t("partners.missions.form.skillsEvaluation", "SKILLS Evaluation")}</FieldLabel>
              <Flex gap="6px" mb="6px">
                <Badge colorScheme="green" variant="subtle">
                  {form.evaluationLanguage}
                </Badge>
                <Badge colorScheme={form.evaluationAutoMode ? "blue" : "purple"} variant="subtle">
                  {form.evaluationAutoMode
                    ? t("partners.missions.form.autoMode", "Auto mode")
                    : `${displaySkills.length} ${t("partners.missions.form.skillsSelected", "skills selected")}`}
                </Badge>
              </Flex>
              <Flex gap="6px" flexWrap="wrap" mt="4px">
                {displaySkills.map((s, i) => (
                  <Tag key={i} size="sm" colorScheme={s.type === "technique" ? "purple" : s.type === "fonctionnel" ? "orange" : "green"} variant="subtle" borderRadius="999px">
                    <TagLabel>{s.technology} ({s.difficulty_level})</TagLabel>
                  </Tag>
                ))}
              </Flex>
            </Box>
          );
        })()}

        {form.purpose === "contact_pool" && (
          <Box>
            <FieldLabel>{t("partners.missions.form.selectedPools", "Selected Pools")}</FieldLabel>
            {selectedPools.length > 0 ? (
              <Flex gap="6px" flexWrap="wrap">
                {selectedPools.map((p) => (
                  <Badge key={p.id} colorScheme="purple" variant="subtle">
                    {p.name} ({p.memberCount ?? 0})
                  </Badge>
                ))}
              </Flex>
            ) : (
              <HelpText>
                {t("partners.missions.form.noPoolsSelected", "No pools selected")}
              </HelpText>
            )}
          </Box>
        )}

        {form.purpose !== "contact_pool" && (
          <Box>
            <FieldLabel>{t("partners.missions.form.selectedPartners", "Selected Partners")}</FieldLabel>
            {selectedPartners.length > 0 ? (
              <Flex gap="6px" flexWrap="wrap">
                {selectedPartners.map((l) => (
                  <Badge key={l.partnerCompanyId} colorScheme="blue" variant="subtle">
                    {l.partnerCompanyName || l.partnerCode}
                  </Badge>
                ))}
              </Flex>
            ) : (
              <HelpText>
                {t("partners.missions.form.noPartnersSelected", "No partner companies selected")}
              </HelpText>
            )}
          </Box>
        )}

        {/* Save as draft toggle — only for new missions */}
        {!isEdit && (
          <Box p="14px" bg="portal.active" borderRadius="10px" borderWidth="1px" borderColor="portal.line">
            <Flex justify="space-between" align="center">
              <Box>
                <SectionTitle>
                  {t("partners.missions.form.saveAsDraft", "Save as draft")}
                </SectionTitle>
                <HelpText mt="4px">
                  {t("partners.missions.form.saveAsDraftDescription", "Save this mission as a draft instead of sending it immediately.")}
                </HelpText>
              </Box>
              <Switch
                colorScheme="green"
                isChecked={!form.sendDirectly}
                onChange={(e) => setForm((prev) => ({ ...prev, sendDirectly: !e.target.checked }))}
                data-testid="send-directly-switch"
              />
            </Flex>
          </Box>
        )}
      </VStack>

      {/* RIGHT: faithful email preview (rendered by the backend). The column
          stretches to the left column's height (parent Flex align="stretch"),
          and the preview box flex-grows to fill it, so the preview is as tall
          as the modal content rather than stopping short. */}
      <Box flex={{ xl: "1 1 0" }} minW={0} display="flex" flexDirection="column">
        <SectionTitle>
          {t("partners.missions.sections.previewTitle", "Email preview")}
        </SectionTitle>
        <HelpText mt="4px" mb="8px">
          {t(
            "partners.missions.sections.previewHelp",
            "This is the email consultants will receive. Edit the content on the left to update it."
          )}
        </HelpText>
        <Box
          borderWidth="1px"
          borderColor="portal.line"
          borderRadius="10px"
          overflow="hidden"
          bg="white"
          position="relative"
          flex="1"
          minH={{ base: "480px", xl: "560px" }}
          maxW="660px"
        >
          {previewError ? (
            <Flex align="center" justify="center" h="100%" p="24px" textAlign="center">
              <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                {t(
                  "partners.missions.sections.previewError",
                  "Could not load the preview. Edit a field to retry."
                )}
              </Text>
            </Flex>
          ) : (
            <>
              <iframe
                title="email-preview"
                srcDoc={previewHtml}
                data-testid="email-preview-iframe"
                // Sandbox the preview: the body may include recruiter-authored HTML,
                // so disallow scripts/same-origin to prevent any injected script from
                // running in the app context.
                sandbox=""
                style={{ width: "100%", height: "100%", border: "none", background: "white" }}
              />
              {previewLoading && (
                <HStack
                  position="absolute"
                  top="8px"
                  right="8px"
                  bg="white"
                  borderWidth="1px"
                  borderColor="portal.line"
                  borderRadius="999px"
                  px="10px"
                  py="4px"
                  spacing="6px"
                  boxShadow="sm"
                >
                  <Spinner size="xs" color="portal.primary" />
                  <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("partners.missions.sections.previewLoading", "Updating preview...")}
                  </Text>
                </HStack>
              )}
            </>
          )}
        </Box>
      </Box>
      </Flex>
    );
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={activeStep === REVIEW_STEP_INDEX ? "6xl" : "3xl"}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {isEdit
            ? t("partners.missions.form.editTitle", "Edit Mission")
            : t("partners.missions.form.createTitle", "Create Mission")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <StepIndicator
            activeStep={activeStep}
            labels={STEPS.map((step) => t(step.titleKey, step.key))}
            onStepClick={(idx) => idx < activeStep && setActiveStep(idx)}
          />

          {renderStepContent()}
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Flex w="100%" justify="space-between">
            <Button
              variant="secondary"
              size="sm"
              onClick={activeStep === 0 ? onClose : goToPrevious}
            >
              {activeStep === 0
                ? t("partners.missions.form.cancel", "Cancel")
                : t("partners.missions.form.back", "Back")}
            </Button>
            <HStack>
              {activeStep < STEPS.length - 1 ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={goToNext}
                  isDisabled={!canProceed()}
                >
                  {t("partners.missions.form.next", "Next")}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                >
                  {isEdit
                    ? t("partners.missions.form.save", "Save")
                    : form.sendDirectly
                    ? t("partners.missions.form.createAndSend", "Create & Send")
                    : t("partners.missions.form.createDraft", "Create Draft")}
                </Button>
              )}
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* RFP overwrite confirmation dialog */}
    <AlertDialog
      isOpen={!!rfpConfirmData}
      leastDestructiveRef={rfpConfirmCancelRef}
      onClose={handleRfpConfirmCancel}
    >
      <AlertDialogOverlay bg="rgba(11,31,58,.45)">
        <AlertDialogContent sx={MODAL_CONTENT_SX}>
          <AlertDialogHeader sx={MODAL_HEADER_SX}>
            {t("partners.missions.form.rfpOverwriteTitle", "Overwrite existing data?")}
          </AlertDialogHeader>
          <AlertDialogBody py="16px">
            <Text fontSize="13px" color="portal.text" sx={{ marginBottom: "0 !important" }}>
              {t("partners.missions.form.rfpOverwriteBody", "The form already contains data. Applying the extracted RFP data will replace the current title, description, skills, and location fields.")}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" ref={rfpConfirmCancelRef} onClick={handleRfpConfirmCancel}>
              {t("partners.missions.form.rfpOverwriteCancel", "Cancel")}
            </Button>
            <Button variant="primary" size="sm" onClick={handleRfpConfirm} ml="8px">
              {t("partners.missions.form.rfpOverwriteConfirm", "Apply RFP data")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
};

export default MissionFormModal;
